import * as React from 'react';
import * as GeolocationService from './Service/GeolocationService';
import * as AuthService from './Service/AuthService';
import * as UserRepository from './Repository/UserRepository';
// import * as SyncService from './Service/SyncService';
import { UserRoles } from './Repository/UserRepository';
import * as TreeRepository from './Repository/TreeRepository';
import * as ContractRepository from './Repository/ContractRepository';
import FormPage from './Pages/FormPage/FormPage';
import MapPage from './Pages/MapPage/MapPage';
import FullPageLoader from './Component/Loader/FullPageLoader';
import AdminUsersPage from './Pages/Admin/Users/AdminUsersPage';
import { Contract, ReactRouterHooks, User } from 'tree-wallet-client';
import { Route, Routes } from 'react-router';
import { ConnectionState, GlobalContext } from './Contexts/GlobalContext';
import { Navigate } from 'react-router-dom';
import { withReactRouter } from './Helper/HooksHelper';
import NewPasswordForm from './Pages/LoginPage/NewPasswordForm';
import ErrorPage from './Pages/ErrorPage/ErrorPage';
import LoginPage from './Pages/LoginPage/LoginPage';
import TestPage from "./Pages/TestPage/TestPage";
import PublicPage from "./Pages/PublicPage/PublicPage";
import AdminContractListPage from './Pages/Admin/Contracts/AdminContractListPage';
import AdminContractEditionPage from './Pages/Admin/Contracts/AdminContractEditionPage';
import Page from "./Component/Page/Page";
import TreeCounter from "./Component/Page/TreeCounter";
import { isImpersonating } from './Service/RoleService';
import { LoaderProvider } from './Contexts/LoaderContext';

const queryString = require('query-string');

interface AppState {
  user?: User;
  contracts?: Contract[];
  contract?: Contract;
  treeCount: number;
  loading: boolean;
  loadingMessage?: string;
  error?: any;
  onlineStatus: ConnectionState;
}

interface AppProps extends ReactRouterHooks {
  user: User;
  contracts: Contract[];
  treeCount: number;
}

class App extends React.Component<AppProps, AppState> {
  declare context: React.ContextType<typeof GlobalContext>;
  static contextType = GlobalContext;
  private counterRef: React.Ref<TreeCounter> = React.createRef();
  private pageRef: React.Ref<Page> = React.createRef();

  constructor(props: AppProps) {
    super(props);
    this.updateState = this.updateState.bind(this);
    this.getContracts = this.getContracts.bind(this);
    this.getCurrentUser = this.getCurrentUser.bind(this);
    this.fetchTreeCount = this.fetchTreeCount.bind(this);
    this.setOnlineStatus = this.setOnlineStatus.bind(this);

    this.state = {
      contracts: props.contracts ? props.contracts : undefined,
      loading: true,
      loadingMessage: undefined,
      user: props.user ? props.user : undefined,
      contract: undefined,
      treeCount: props.treeCount ? props.treeCount : 0,
      error: undefined,
      onlineStatus: navigator.onLine ? ConnectionState.online : ConnectionState.offline
    };
  }

  updateState(values: AppState) {
    console.log('App::updateState', { values });
    this.setState(values);
  }

  setOnlineStatus() {
    const status = navigator.onLine ? ConnectionState.online : ConnectionState.offline;
    console.log('Setting onlineStatus: ' + navigator.onLine)
    this.setState({ onlineStatus: status });
  }

  componentWillUnmount() {
    removeEventListener('online', this.setOnlineStatus);
    removeEventListener('offline', this.setOnlineStatus);
  }

  async componentDidMount() {
    this.setState({ loading: true, loadingMessage: "Loading..." });
    await this.getCurrentUser();
    const contracts = await this.getContracts();
    
    /*
    if (contracts && contracts.length > 0) {
      await SyncService.syncValidatorPhotos(contracts, (message: string) => {
        this.setState({ loadingMessage: message });
      });
    }
    */

    await this.fetchTreeCount(undefined, contracts && contracts[0] && contracts[0].id);

    addEventListener('online', this.setOnlineStatus);
    addEventListener('offline', this.setOnlineStatus);

    this.setState({ loading: false, loadingMessage: undefined });
  }

  async getContracts(): Promise<Contract[] | undefined> {
    const { contracts: contractsFromContext } = this.context;

    if (contractsFromContext) {
      return contractsFromContext;
    }

    return await ContractRepository.getContractsList({
      successCallback: (contracts: Contract[]) => {
        this.setState({
          contracts,
          contract: contracts[0],
        })
      }
    });
  }

  async getCurrentUser() {
    const { user: userFromContext } = this.context;

    const user = await AuthService.getUser();
    if (user) {
      console.info('App::getCurrentUser', 'found user', user);
      this.setState({ user });
      await GeolocationService.watchPosition();
      return;
    } else {
      console.warn('App::getCurrentUser', 'no user found');
    }

    if (!userFromContext) {
      await UserRepository.getUser({
        successCallback: async (user: User) => {
          AuthService.setUser(user);
          await GeolocationService.watchPosition();
          this.setState({ user });
        },
        errorCallback: (error: any) => {
          this.setState({ error });
        },
      });
    }
  }

  // 'refresh' parameter used by TreeMap when a new tree count needs to be displayed: 
  // the tree counter applied is applied locally without new request to the server (useful after a deletion in TreeMap)
  async fetchTreeCount(increment?: number, contractId?: any, publicTrees?: boolean, refresh: boolean = true) {
    console.log('App::fetchTreeCount', { increment, contractId, publicTrees, refresh })
    const { treeCount: treesFromContext } = this.context;
    const { user } = this.state;

    if (publicTrees || !user) {
      this.setState({ treeCount: increment || 0 });
      return;
    }

    if (increment) {
      this.incrementCounter(increment);
    }

    if (!treesFromContext && refresh) {
      try {
        const treeCount = await TreeRepository.getTreeCount({
          params: {
            contractId: contractId
          },
        });
        console.log('App::fetchTreeCount', 'fetched tree count', { treeCount })
        this.setState({ treeCount: parseInt(String(treeCount.trees)) });
      } catch (err) {
        // The count is left as it is. The storage counter this used to fall back on is a per-device
        // figure, not the contract's count: writing it here replaced a good count with a wrong one.
        console.error('could not fetch tree count from server', { err });
      }
    }
  }

  incrementCounter(increment: number) {
    const { treeCount } = this.state;
    const { treeCount: treesFromContext } = this.context;

    if (treesFromContext) {
      this.context = {
        ...this.context,
        treeCount: parseInt(String(treesFromContext)) + increment
      };
      return;
    }

    const getTreeCount = (treeCount: any) => {
      const parsed = parseInt(treeCount);
      if (!isNaN(parsed)) {
        return parsed;
      }
      return 0
    }

    this.setState({ treeCount: getTreeCount(treeCount) + parseInt(String(increment)) });
    return;
  }

  render() {
    const { navigate, location, params } = this.props;
    const { user, contract, loading, treeCount, contracts, onlineStatus } = this.state;
    const pageProps = { loading, onlineStatus, treeCount };
    const urlParams = queryString.parse(location.search, { arrayFormat: 'comma', arrayFormatSeparator: '|' });

    if (user && urlParams?.redirect) {
      return <Navigate to={`${urlParams.redirect}`} />;
    }

    if (loading) {
      return <FullPageLoader message={this.state.loadingMessage} />;
    }

    const getHomepage = (user?: User, contract?: Contract) => {
      if (!user) {
        return <Navigate to='/login' replace />;
      }

      switch (true) {
        case ContractRepository.hasRole(contract && contract.roles, ContractRepository.roles.TreePlanter.name):
          return <Navigate to={`/${contract?.id}/form`} replace />;
        case UserRepository.userHasRole(user.roles, UserRoles.Admin.name):
          return <AdminUsersPage {...pageProps} />;
        default:
          return <Navigate to={`/${contract?.id}/list`} replace />;
      }
    };

    return (
      <GlobalContext.Provider value={{
        location,
        params,
        navigate,
        user,
        contract,
        treeCount,
        contracts,
        onlineStatus,
        counterRef: this.counterRef,
        impersonating: isImpersonating(),
        updateState: this.updateState,
        updateTrees: this.fetchTreeCount
      }}>
        <LoaderProvider>
          <Routes>
            <Route path="/">
              <Route path="reset-password/:token" element={<NewPasswordForm />} />
              <Route path='test' element={<TestPage />} />
              <Route path='public' element={<PublicPage />} />
              <Route path="login" element={<LoginPage originalLocation={location} />} />
              <Route path="admin">
                <Route index element={<AdminUsersPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="contracts" element={<AdminContractListPage />} />
                <Route path="contracts/edit/:contractId" element={<AdminContractEditionPage />} />
              </Route>
              <Route path=":contractId/form" element={<FormPage {...pageProps} />} />
              <Route path=":contractId/list" element={<MapPage {...pageProps} />} />
              <Route index element={getHomepage(user, contract)} />
            </Route>
            <Route path='unauthorized' element={<Page treeCount={treeCount} isPublic={true} loading={false}><ErrorPage template={403} /></Page>} />
            <Route path='*' element={<Page treeCount={treeCount} isPublic={true} loading={false}><ErrorPage template={404} /></Page>} />
          </Routes>
        </LoaderProvider>
      </GlobalContext.Provider>
    );
  }
}

export default withReactRouter(App);
